/**
 * MyClips V3 — L-16: useClipDeepLink
 *
 * URL'den `/c/:clipId` parse edip mount'ta feed'e anchor et. Browser
 * back/forward history.back dinler.
 *
 * Backend: Firebase Hosting rewrite `/c/** → renderClipShare` Cloud
 * Function OG HTML + client redirect döner. Gerçek kullanıcı tarayıcısı
 * JS açıldığında bu hook URL'i okur + içerdeki clipId'yi event olarak
 * dispatch eder (mlv3:anchor-clip).
 *
 * `/c/` prefix'i Mylasa ana routing'ine çakışmıyor (PR-17: legacy
 * permalinkBoot kaldırıldı; /c/{id} pathname'ini App resolvePathToPage çözer).
 */

import { useEffect } from "react";

const CLIP_PATH_RE = /^\/c\/([a-zA-Z0-9_-]{8,40})\/?$/;
const CLIP_ID_RE = /^[a-zA-Z0-9_-]{8,40}$/;

/**
 * URL'den clipId çıkar (SAF / test-edilebilir). İki kaynak:
 *  - pathname `/c/{clipId}` (canonical paylaşım linki)
 *  - query `?c={clipId}` (clip-share function'ın insan-redirect hedefi —
 *    /c/ doğrudan açılınca function'a takılıp döngü oluyordu; SPA-routed
 *    /?c= ile boot edip burada okunur).
 */
export function parseClipIdFromUrl(
  pathname: string | null | undefined,
  search: string | null | undefined,
): string | null {
  try {
    const m = CLIP_PATH_RE.exec(pathname || "");
    if (m) return m[1];
    const sp = new URLSearchParams(search || "");
    const q = sp.get("c");
    if (q && CLIP_ID_RE.test(q)) return q;
  } catch (_) {}
  return null;
}

/**
 * URL'den clipId döner, yoksa null.
 */
export function readClipIdFromLocation() {
  if (typeof window === "undefined") return null;
  return parseClipIdFromUrl(
    window.location.pathname || "",
    window.location.search || "",
  );
}

/**
 * Mount'ta + popstate'te URL'den clipId oku, handler'a ilet.
 */
export function useClipDeepLink(handler: ((clipId: string) => void) | null | undefined) {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const fire = () => {
      const id = readClipIdFromLocation();
      if (id) {
        try { handler?.(id); } catch (_) {}
      }
    };
    // İlk mount
    fire();
    const onPop = () => fire();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [handler]);
}

/**
 * "Linki kopyala" + share sheet için absolute URL.
 */
export function buildClipUrl(clipId: string | null | undefined): string {
  if (!clipId) return "";
  const origin =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "https://mylasa.app";
  return `${origin}/c/${encodeURIComponent(clipId)}`;
}

/**
 * Feed-level: kullanıcı bir klibe anchor ederken URL'i pushState ile
 * güncelle. Paylaşırsa doğru link kopyalar.
 */
export function pushClipUrl(clipId: string | null | undefined): void {
  if (!clipId) return;
  if (typeof window === "undefined") return;
  try {
    const target = `/c/${encodeURIComponent(clipId)}`;
    if (window.location.pathname !== target) {
      window.history.pushState({ mlv3: "clip", clipId }, "", target);
    }
  } catch (_) {}
}

export default useClipDeepLink;
